import React, { useEffect } from "react";
import { Controller } from "react-hook-form";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useBusinessProfileFilters from "../../resources/businessProfiles/components/BusinessProfilesFilterHelper";
import { useGetBusinessProfilesHandler } from "../../resources/businessProfiles/dataHandlers/getBusinessProfilesHandler";
import { isBusinessProfileRegistered } from "../../constants/businessProfileStatusConstants.jsx";

const UNREGISTERED_PROFILE_MESSAGE =
  "This profile is not registered on Oman E-Invoicing Network, go on the profile section and register it.";

const getBusinessProfileOptionLabel = (option) =>
{
    if(option?.postal_address?.country_code === "OM"){
        return `${option?.name || ""} | ${option?.email || ""} | ${option?.party_tax_scheme?.company_id || ""}`;
    }
    else{
        return `${option?.name || ""} | ${option?.email || ""} `;
    }
}


const getDisabledReason = (profile) => {
  if (!isBusinessProfileRegistered(profile)) {
    return UNREGISTERED_PROFILE_MESSAGE;
  }
  return "";
};

const BusinessProfileSelect = ({
  label = "Billed by",
  name,
  control,
  errors,
  readOnly = false,
  onProfilesLoaded,
  getOptionLabel = getBusinessProfileOptionLabel,
}) => {
  const { currentQuery, handleSearchChange } = useBusinessProfileFilters({
    defaultQuery: {},
    searchOnChange: true,
  });

  const { businessProfiles, isLoading } = useGetBusinessProfilesHandler({
    searchQuery: currentQuery,
  });

  useEffect(() => {
    if (businessProfiles) {
      onProfilesLoaded?.(businessProfiles);
    }
  }, [businessProfiles, onProfilesLoaded]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Autocomplete
            loading={isLoading}
            id={name}
            fullWidth
            readOnly={readOnly}
            options={businessProfiles}
            getOptionLabel={getOptionLabel}
            getOptionKey={(option) => option?.id}
            getOptionDisabled={(option) => Boolean(getDisabledReason(option))}
            renderOption={(props, option) => {
              const { key, onClick, onMouseDown, ...optionProps } = props;
              const disabledReason = getDisabledReason(option);
              const unregistered = Boolean(disabledReason);
              const blockDisabledSelection = (event) => {
                event.preventDefault();
                event.stopPropagation();
              };
              const optionLabel = (
                <>
                  {unregistered && (
                    <ErrorOutlineIcon
                      sx={{ color: "error.main", fontSize: 18, flexShrink: 0 }}
                      aria-hidden
                    />
                  )}{" "}
                  {getOptionLabel(option)}
                </>
              );

              return (
                <Box
                  key={key}
                  component="li"
                  {...optionProps}
                  onMouseDown={
                    disabledReason ? blockDisabledSelection : onMouseDown
                  }
                  onClick={disabledReason ? blockDisabledSelection : onClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    opacity: disabledReason ? 0.5 : 1,
                    cursor: disabledReason ? "not-allowed" : "pointer",
                  }}
                >
                  {disabledReason ? (
                    <Tooltip title={disabledReason} placement="right">
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          pointerEvents: "auto",
                        }}
                      >
                        {optionLabel}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {optionLabel}
                    </Box>
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!errors}
                helperText={errors || ""}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => {
              if (value && getDisabledReason(value)) {
                return;
              }
              field.onChange(value);
            }}
            onBlur={field.onBlur}
            name={field.name}
            ref={field.ref}
            value={field.value || null}
            onInputChange={(event, value, reason) => {
              if (reason === "input") {
                handleSearchChange(event);
              }
            }}
            filterOptions={(x) => x}
          />
        </Box>
      )}
    />
  );
};

export default BusinessProfileSelect;
